import React, { useState } from 'react';
import { Button, Image, Popconfirm, Space, Spin, notification } from 'antd';
import VehicleFile from '../../../core/models/VehicleFile';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDeleteImageMutation } from '../../../app/api/vehicleApiSlice';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { closestCenter, DndContext, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
interface IProps {
  vehicleId?: string;
  images: VehicleFile[] | null;
  delible?: boolean;
  sortable?: boolean;
  onSortEnd?: (images: VehicleFile[]) => void;
}

const SortableItem = ({
  id,
  vehicleId,
  imageUrl,
  delible,
}: {
  id: string;
  vehicleId?: string;
  imageUrl: string;
  delible?: boolean;
}): JSX.Element => {
  const [deleteImage] = useDeleteImageMutation();

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '150px',
    height: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '5px',
    backgroundColor: '#f0f0f0',
  };

  const handleDelete = (vehicleId: string | undefined, imageId: string | undefined): void => {
    if (vehicleId && imageId)
      deleteImage({ vehicleId, imageId })
        .unwrap()
        .then((response) => {
          notification.success({
            message: 'Success',
            description: 'Image deleted successfully',
          });
        })
        .catch((error: any) => {
          console.log(error);
        });
  };

  return (
    <div style={{ position: 'relative' }} key={`div-${id}`}>
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Image
          src={imageUrl}
          alt={`Item ${id}`}
          style={{ width: '100%', height: '100%' }}
          preview={false}
        />
      </div>
      {delible && (
        <Popconfirm
          key={`confirm-${id}`}
          title='Are you sure to delete this image?'
          onConfirm={() => handleDelete(vehicleId, id)}
          okText='Yes'
          cancelText='No'
        >
          <Button
            key={`button-${id}`}
            type='primary'
            danger
            icon={<DeleteOutlined />}
            shape='circle'
            color='#f00'
            size='small'
            style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              zIndex: '1000',
            }}
          />
        </Popconfirm>
      )}
    </div>
  );
};

const ViewVehiclePhotos: React.FC<IProps> = ({
  vehicleId,
  images,
  delible,
  sortable,
  onSortEnd,
}: IProps) => {
  const [items, setItems] = useState(images ?? []);

  // update items when images prop changes
  React.useEffect(() => {
    setItems(images ?? []);
  }, [images]);

  function handleDragEnd(event: { active: any; over: any }): void {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const sortedItems = arrayMove(items, oldIndex, newIndex);

      setItems(sortedItems);

      if (sortable && onSortEnd) onSortEnd(sortedItems);
    }
  }

  return (
    <>
      <Image.PreviewGroup>
        <Space size={[8, 16]} wrap>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={items.map((item) => item.id!)}
              strategy={rectSortingStrategy}
              disabled={!sortable}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {items.map((item, index) => (
                  <SortableItem
                    key={item.id}
                    id={item.id!}
                    vehicleId={vehicleId}
                    imageUrl={item.filePath}
                    delible={delible}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </Space>
      </Image.PreviewGroup>
    </>
  );
};

export default ViewVehiclePhotos;
